/**
 * Document lifecycle on the frontend: Open/Partial/Closed status, conversion-chain nesting,
 * "שיוך מסמכים" helpers, and the create-page pending-links hook.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

export const QUOTATION_TYPE = 1;
export const ORDER_TYPE = 2;
export const TAX_INVOICE_TYPE = 4;
export const INVOICE_RECEIPT_TYPE = 6;
export const RECEIPT_TYPE = 7;
/** ForeignCurrencyCode.ILS — enum 0 is USD, so a missing form currency must not be sent as 0. */
export const CURRENCY_ILS = 14;
export const resolveLinkCurrency = (currency) =>
    (currency === 0 || Number(currency) > 0) ? Number(currency) : CURRENCY_ILS;
export const SOURCE_CHILD_TYPES = new Set([1, 2, 3, 11]); // Quotation, Order, Transaction, DeliveryNote

/**
 * Three-state document lifecycle status, mirroring the backend DocumentLifecycleStatus enum.
 * The legacy boolean `status` is only a projection (true when Closed) and is used as a fallback
 * for rows that do not carry `processingStatus`.
 */
export const LIFECYCLE_STATUS = Object.freeze({
    OPEN: 0,
    PARTIAL: 1,
    CLOSED: 2,
});

/** Mirrors backend DocumentItemMatchStatus. ExactMatch meaning depends on ITEM_MATCH_MODE. */
export const ITEM_MATCH_STATUS = Object.freeze({
    UNKNOWN: 0,
    MISMATCH: 1,
    AMOUNT_ONLY: 2,
    PARTIAL: 3,
    EXACT: 4,
    AMOUNT_MISMATCH: 5,
});

/** Mirrors backend DocumentItemMatchMode. */
export const ITEM_MATCH_MODE = Object.freeze({
    /** TaxInvoice / InvoiceReceipt ← Quotation/Order/Transaction/DeliveryNote: items + quantities. */
    QUANTITY_AND_ITEMS: 'quantityAndItems',
    /** Receipt ↔ TaxInvoice: items + amounts. */
    AMOUNT_AND_ITEMS: 'amountAndItems',
    /** Legacy bags + amount ExactMatch (default Compare when mode omitted). */
    QUANTITY_AMOUNT_AND_ITEMS: 'quantityAmountAndItems',
});

/** Pick ExactMatch rule from relation (+ document type kept for call-site compatibility). */
export const resolveItemMatchMode = (relationType, _documentType) => {
    if (Number(relationType) === RELATION_TYPE.PAYMENT_FOR) return ITEM_MATCH_MODE.AMOUNT_AND_ITEMS;
    // TaxInvoice / InvoiceReceipt ← sources, and source→source: quantity + items (discounts OK).
    return ITEM_MATCH_MODE.QUANTITY_AND_ITEMS;
};

export const getProcessingStatus = (row) => {
    const value = row?.processingStatus ?? row?.ProcessingStatus;
    if (value === LIFECYCLE_STATUS.OPEN || value === LIFECYCLE_STATUS.PARTIAL || value === LIFECYCLE_STATUS.CLOSED) {
        return value;
    }
    return row?.status ? LIFECYCLE_STATUS.CLOSED : LIFECYCLE_STATUS.OPEN;
};

/** Filter / sort key: 'open' | 'partial' | 'closed'. */
export const getStatusKey = (row) => {
    switch (getProcessingStatus(row)) {
        case LIFECYCLE_STATUS.CLOSED: return 'closed';
        case LIFECYCLE_STATUS.PARTIAL: return 'partial';
        default: return 'open';
    }
};

export const getPartialStatusLabelKey = (documentType) => {
    switch (Number(documentType)) {
        case ORDER_TYPE: return 'DocumentStatusPartiallyInvoiced';
        // Tax invoice: partial when receipts have not covered it yet. Receipts are terminal (Closed).
        case TAX_INVOICE_TYPE:
        case RECEIPT_TYPE: return 'DocumentStatusPartiallyPaid';
        default: return 'DocumentStatusPartiallyProcessed';
    }
};

export const getStatusLabelKey = (row) => {
    switch (getProcessingStatus(row)) {
        case LIFECYCLE_STATUS.CLOSED: return 'DocumentStatusClosed';
        case LIFECYCLE_STATUS.PARTIAL: return getPartialStatusLabelKey(row?.transactionDocumentType);
        default: return 'DocumentStatusOpen';
    }
};

/** CSS class for the status chip. */
export const getStatusClassName = (row) => {
    switch (getProcessingStatus(row)) {
        case LIFECYCLE_STATUS.CLOSED: return 'processed';
        case LIFECYCLE_STATUS.PARTIAL: return 'partially-processed';
        default: return 'unprocessed';
    }
};

/** Sort rank so Open < Partial < Closed. */
export const getStatusSortRank = (row) => getProcessingStatus(row);

const formatAmount = (value) => {
    if (value == null || Number.isNaN(Number(value))) return null;
    return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Original / applied / remaining amounts for the status tooltip, or null when the row carries no
 * amount summary. Amounts are in the document's own basis (`amountCurrency`).
 */
export const getLifecycleAmounts = (row) => {
    const original = row?.originalAmount ?? row?.OriginalAmount;
    if (original == null) return null;
    return {
        original: formatAmount(original),
        applied: formatAmount(row?.appliedAmount ?? row?.AppliedAmount ?? 0),
        remaining: formatAmount(row?.remainingAmount ?? row?.RemainingAmount),
        hasUnresolvedAllocation: !!(row?.hasUnresolvedAllocation ?? row?.HasUnresolvedAllocation),
    };
};

/** Over-allocation 409 body returned by the backend: { code, message, violations[] }. */
export const OVER_ALLOCATION_ERROR_CODE = 'DocumentOverAllocation';

export const parseOverAllocationError = (body) => {
    if (!body) return null;
    let parsed = body;
    if (typeof body === 'string') {
        try {
            parsed = JSON.parse(body);
        } catch {
            return null;
        }
    }
    const code = parsed?.code ?? parsed?.Code;
    if (code !== OVER_ALLOCATION_ERROR_CODE) return null;
    return {
        message: parsed?.message ?? parsed?.Message ?? '',
        violations: parsed?.violations ?? parsed?.Violations ?? [],
    };
};

export const formatOverAllocationMessage = (t, error) => {
    const details = (error?.violations || []).map((v) => t('DocumentOverAllocationDetail', {
        original: formatAmount(v.originalAmount ?? v.OriginalAmount) ?? '-',
        applied: formatAmount(v.previouslyApplied ?? v.PreviouslyApplied) ?? '-',
        remaining: formatAmount(v.remainingAmount ?? v.RemainingAmount) ?? '-',
        requested: formatAmount(v.requestedAmount ?? v.RequestedAmount) ?? '-',
    }));
    return [t('DocumentOverAllocationError'), ...details].join('\n');
};

/**
 * Per-source allocations for a multi-source generation, in each source document's own amount
 * basis (VAT-inclusive). Merged items are tagged with `sourceDocumentId` and normalized to
 * VAT-inclusive prices per source, so each source's share is the sum of its lines. A share within
 * the rounding slack of the source amount snaps to the source amount so an unedited conversion
 * closes the source exactly.
 */
export const ALLOCATION_ROUNDING_SLACK = 0.05;

export const buildSourceAllocations = (items = [], sourceDocs = []) => {
    const totals = new Map();
    items.forEach((item) => {
        const sourceId = item?.sourceDocumentId;
        if (sourceId == null) return;
        const price = Number(item.price) || 0;
        const discount = Number(item.discount) || 0;
        const quantity = Number(item.quantity) || 0;
        const line = (price - (price * discount) / 100) * quantity;
        const key = String(sourceId);
        totals.set(key, (totals.get(key) || 0) + line);
    });
    if (totals.size === 0) return [];

    return sourceDocs
        .filter((d) => d?.id != null && totals.has(String(d.id)))
        .map((d) => {
            let amount = Math.round(totals.get(String(d.id)) * 100) / 100;
            const sourceAmount = Math.abs(Number(d.amount) || 0);
            if (sourceAmount > 0 && Math.abs(amount - sourceAmount) <= ALLOCATION_ROUNDING_SLACK) {
                amount = sourceAmount;
            }
            return { sourceDocumentId: d.id, amount: Math.max(0, amount) };
        });
};

/**
 * Conversion-chain helpers for Transaction Documents.
 *
 * Nesting is rooted at Receipt (7) or InvoiceReceipt (6) that closed a source flow:
 *   ExemptDealer:          Receipt → Order/Quotation/Transaction/DeliveryNote
 *   Other case types:      Receipt → TaxInvoice → Order/Quotation/Transaction/DeliveryNote
 *   InvoiceReceipt:        InvoiceReceipt → Order/Quotation/Transaction/DeliveryNote
 *                          (or InvoiceReceipt → TaxInvoice → those sources)
 *
 * Direct-created Receipt / InvoiceReceipt (no sources, no related invoice) stay flat.
 *
 * A TaxInvoice that no receipt has paid yet is the root of its own sources
 * (TaxInvoice → Order …). A TaxInvoice paid by several receipts nests under the first
 * receipt linked to it, after the later receipts (Receipt 1 → Receipt 2, TaxInvoice → Order …).
 * Source documents not invoiced yet group under the latest one (Order → Quotation).
 */

export const parseSourceDocumentIds = (ids) => {
    if (!ids) return [];
    if (Array.isArray(ids)) return ids.map(String).filter(Boolean);
    if (typeof ids === 'string') {
        try {
            const parsed = JSON.parse(ids);
            return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
        } catch {
            return [];
        }
    }
    return [];
};

const rowId = (row) => (row?.id != null ? String(row.id) : row?.Id != null ? String(row.Id) : '');

const rowType = (row) => Number(row?.transactionDocumentType ?? row?.TransactionDocumentType);

const rowSourceIds = (row) => parseSourceDocumentIds(row?.sourceDocumentIds || row?.SourceDocumentIds);

const rowRelatedIncomeId = (row) => {
    const value = row?.relatedIncomeId ?? row?.RelatedIncomeId;
    return value ? String(value) : null;
};

const rowIsCanceled = (row) => !!(row?.isCanceled ?? row?.IsCanceled);

const isReceiptFamily = (row) => {
    const type = rowType(row);
    return type === RECEIPT_TYPE || type === INVOICE_RECEIPT_TYPE;
};

/** Documents that invoice a source; a source invoiced in parts has several of them. */
const CLOSING_TARGET_TYPES = new Set([TAX_INVOICE_TYPE, INVOICE_RECEIPT_TYPE]);

const isChainRoot = (row) => isReceiptFamily(row) || rowType(row) === TAX_INVOICE_TYPE || SOURCE_CHILD_TYPES.has(rowType(row));

const rowPaymentIds = (row) => parseSourceDocumentIds(row?.paymentDocumentIds || row?.PaymentDocumentIds);

/**
 * TaxInvoice ids paid by more than one live Receipt, with those receipts in link order
 * (the invoice's paymentDocumentIds, first linked first; unknown ones after, in row order).
 */
const sharedInvoicePayers = (rows, byId) => {
    const payersOf = new Map();
    for (const row of rows) {
        if (rowType(row) !== RECEIPT_TYPE || rowIsCanceled(row)) continue;
        const relatedId = rowRelatedIncomeId(row);
        const invoiceIds = new Set([...(relatedId ? [relatedId] : []), ...rowSourceIds(row)]);
        for (const invoiceId of invoiceIds) {
            const invoice = byId.get(invoiceId);
            if (!invoice || rowType(invoice) !== TAX_INVOICE_TYPE || rowIsCanceled(invoice)) continue;
            if (!payersOf.has(invoiceId)) payersOf.set(invoiceId, []);
            payersOf.get(invoiceId).push(rowId(row));
        }
    }
    const shared = new Map();
    for (const [invoiceId, payers] of payersOf) {
        if (payers.length < 2) continue;
        const order = rowPaymentIds(byId.get(invoiceId));
        const rank = (id) => {
            const index = order.indexOf(id);
            return index === -1 ? order.length : index;
        };
        shared.set(invoiceId, payers
            .map((id, index) => ({ id, index }))
            .sort((a, b) => rank(a.id) - rank(b.id) || a.index - b.index)
            .map(({ id }) => id));
    }
    return shared;
};

const addChild = (parentOf, childrenOf, parentId, childId) => {
    if (!parentId || !childId || parentId === childId) return;
    if (parentOf.has(childId)) return;
    for (let up = parentOf.get(parentId), guard = 0; up && guard < 1000; up = parentOf.get(up), guard += 1) {
        if (up === childId) return;
    }
    parentOf.set(childId, parentId);
    if (!childrenOf.has(parentId)) childrenOf.set(parentId, []);
    childrenOf.get(parentId).push(childId);
};

/**
 * Build parent/child maps. Receipt / InvoiceReceipt and TaxInvoice may be roots; a TaxInvoice
 * paid by a single receipt nests under that receipt together with its sources.
 */
export const buildConversionForest = (rows = []) => {
    const byId = new Map(rows.map((r) => [rowId(r), r]));
    const parentOf = new Map();
    const childrenOf = new Map();
    // Invoices grouped beside another invoice of the same source (not its sources).
    const continuations = new Set();

    // Intermediate sources nest recursively (TaxInvoice → Order → Quotation).
    const attachSourcesUnder = (parentId, sourceIds, seen = new Set([parentId])) => {
        for (const sid of sourceIds) {
            if (!byId.has(sid) || seen.has(sid)) continue;
            seen.add(sid);
            addChild(parentOf, childrenOf, parentId, sid);
            const child = byId.get(sid);
            if (SOURCE_CHILD_TYPES.has(rowType(child))) {
                attachSourcesUnder(sid, rowSourceIds(child), seen);
            }
        }
    };

    // First receipt → the later receipts → the invoice (→ its sources).
    const shared = sharedInvoicePayers(rows, byId);
    for (const [invoiceId, [firstId, ...laterIds]] of shared) {
        for (const payerId of laterIds) addChild(parentOf, childrenOf, firstId, payerId);
        addChild(parentOf, childrenOf, firstId, invoiceId);
        attachSourcesUnder(invoiceId, rowSourceIds(byId.get(invoiceId)));
    }

    for (const row of rows) {
        if (!isReceiptFamily(row) || rowIsCanceled(row)) continue;
        const id = rowId(row);
        if (!id) continue;
        const type = rowType(row);
        const sourceIds = rowSourceIds(row).filter((sid) => !shared.has(sid));
        const relatedId = rowRelatedIncomeId(row);

        if (type === RECEIPT_TYPE && relatedId && byId.has(relatedId) && !shared.has(relatedId)) {
            addChild(parentOf, childrenOf, id, relatedId);
            const taxInvoice = byId.get(relatedId);
            attachSourcesUnder(relatedId, rowSourceIds(taxInvoice));
        }

        for (const sid of sourceIds) {
            if (!byId.has(sid)) continue;
            addChild(parentOf, childrenOf, id, sid);
            const child = byId.get(sid);
            const childType = rowType(child);
            if (childType === TAX_INVOICE_TYPE || SOURCE_CHILD_TYPES.has(childType)) {
                attachSourcesUnder(sid, rowSourceIds(child), new Set([id, sid]));
            }
        }
    }

    // Invoices no single receipt took: root of their own sources.
    for (const row of rows) {
        if (rowType(row) !== TAX_INVOICE_TYPE || rowIsCanceled(row)) continue;
        const id = rowId(row);
        if (!id || parentOf.has(id)) continue;
        attachSourcesUnder(id, rowSourceIds(row));
    }

    // A source invoiced in parts: its other invoices join the group of the invoice that holds it,
    // before the source rows (TaxInvoice 1 → TaxInvoice 2, Order … ; same for InvoiceReceipts).
    for (const row of rows) {
        if (!CLOSING_TARGET_TYPES.has(rowType(row)) || rowIsCanceled(row)) continue;
        const id = rowId(row);
        if (!id || parentOf.has(id)) continue;
        const holderId = rowSourceIds(row)
            .filter((sid) => byId.has(sid) && SOURCE_CHILD_TYPES.has(rowType(byId.get(sid))))
            .map((sid) => parentOf.get(sid))
            .find((pid) => pid && pid !== id && !SOURCE_CHILD_TYPES.has(rowType(byId.get(pid))));
        if (!holderId) continue;
        addChild(parentOf, childrenOf, holderId, id);
        if (parentOf.get(id) !== holderId) continue;
        continuations.add(id);
        const siblings = childrenOf.get(holderId) || [];
        if (siblings[siblings.length - 1] === id) {
            siblings.pop();
            const at = siblings.findIndex((cid) => SOURCE_CHILD_TYPES.has(rowType(byId.get(cid))));
            siblings.splice(at < 0 ? siblings.length : at, 0, id);
        }
    }

    // Source documents not invoiced yet: the latest one is the root (Order → Quotation).
    for (const row of rows) {
        if (!SOURCE_CHILD_TYPES.has(rowType(row)) || rowIsCanceled(row)) continue;
        const id = rowId(row);
        if (!id || parentOf.has(id)) continue;
        attachSourcesUnder(id, rowSourceIds(row));
    }

    return { parentOf, childrenOf, byId, continuations };
};

const findConversionRootId = (id, parentOf) => {
    let current = id;
    const seen = new Set();
    while (parentOf.has(current) && !seen.has(current)) {
        seen.add(current);
        current = parentOf.get(current);
    }
    return current;
};

/**
 * Receipt / InvoiceReceipt ids that should be shown (and auto-expanded) because a
 * filtered/searched TaxInvoice or other source sits under them.
 */
export const getHoistedReceiptRootIds = (visibleRows = [], allRows = visibleRows) => {
    const { parentOf, byId } = buildConversionForest(allRows);
    const ids = [];
    const seen = new Set();
    for (const row of visibleRows) {
        const id = rowId(row);
        if (!id) continue;
        const rootId = findConversionRootId(id, parentOf);
        if (rootId === id || seen.has(rootId)) continue;
        const root = byId.get(rootId);
        if (!root || rowIsCanceled(root) || !isChainRoot(root)) continue;
        seen.add(rootId);
        ids.push(rootId);
    }
    return ids;
};

export const flattenConversionRows = (visibleRows = [], expandedMap = {}, allRows = visibleRows) => {
    const { parentOf, childrenOf, byId } = buildConversionForest(allRows);
    const visibleIds = new Set(visibleRows.map((r) => rowId(r)).filter(Boolean));
    const emitted = new Set();

    const walk = (id, depth, ancestorExpanded) => {
        const row = byId.get(id);
        if (!row) return [];
        const kids = childrenOf.get(id) || [];
        const expanded = ancestorExpanded || (kids.length > 0 && expandedMap[id] === true);
        const out = [{
            ...row,
            conversionDepth: depth,
            // Only the visible root gets an arrow; expanding it opens the whole chain.
            conversionHasChildren: kids.length > 0 && depth === 0,
            conversionExpanded: expanded,
        }];
        if (expanded) {
            for (const cid of kids) out.push(...walk(cid, depth + 1, true));
        }
        return out;
    };

    const result = [];
    for (const row of visibleRows) {
        const id = rowId(row);
        if (!id) continue;

        const rootId = findConversionRootId(id, parentOf);
        const root = byId.get(rootId);
        const hoistToReceipt = rootId !== id
            && root
            && !rowIsCanceled(root)
            && isChainRoot(root);

        const startId = hoistToReceipt ? rootId : id;
        if (emitted.has(startId)) continue;

        if (!hoistToReceipt) {
            const parentId = parentOf.get(id);
            // Keep a source as its own row when it is not under a live Receipt / InvoiceReceipt.
            if (parentId && visibleIds.has(parentId)) continue;
        }

        emitted.add(startId);
        result.push(...walk(startId, 0, false));
    }
    return result;
};

/** True when targetId is reachable from the receipt through its related invoice / source chain. */
const receiptClaimsId = (receipt, targetId, byId) => {
    const seen = new Set([rowId(receipt)]);
    const relatedId = rowRelatedIncomeId(receipt);
    const stack = [...rowSourceIds(receipt), ...(relatedId ? [relatedId] : [])];
    while (stack.length > 0) {
        const id = stack.pop();
        if (id === targetId) return true;
        if (seen.has(id)) continue;
        seen.add(id);
        const row = byId.get(id);
        if (row) stack.push(...rowSourceIds(row));
    }
    return false;
};

/**
 * Linked-list cancel: a nested source cannot be canceled while a live Receipt /
 * InvoiceReceipt / TaxInvoice sits above it in the group. The group root stays
 * cancelable (cancel the invoice, not the Order nested under it).
 */
export const isConversionCancelBlocked = (row, allRows = []) => {
    const id = rowId(row);
    if (!id || rowIsCanceled(row)) return false;

    const byId = new Map(allRows.map((r) => [rowId(r), r]));
    if (allRows.some((r) => !rowIsCanceled(r) && isReceiptFamily(r) && receiptClaimsId(r, id, byId))) {
        return true;
    }

    const { parentOf, continuations } = buildConversionForest(allRows);
    // Another invoice of the same partly invoiced source: grouped beside it, not a source of it.
    if (continuations.has(id)) return false;
    for (let current = parentOf.get(id), guard = 0; current && guard < 1000; current = parentOf.get(current), guard += 1) {
        const ancestor = byId.get(current);
        if (!ancestor || rowIsCanceled(ancestor)) continue;
        if (rowType(ancestor) === TAX_INVOICE_TYPE || isReceiptFamily(ancestor)) return true;
    }
    return false;
};

/**
 * Direct, not-canceled descendants under this row in the conversion forest. Receipts grouped
 * with another receipt of the same invoice are not its sources and are left out.
 */
export const getConversionDescendants = (row, allRows = []) => {
    const { childrenOf, byId, continuations } = buildConversionForest(allRows);
    const startId = rowId(row);
    if (!startId) return [];

    const out = [];
    const walk = (id) => {
        for (const cid of childrenOf.get(id) || []) {
            const child = byId.get(cid);
            if (!child) continue;
            if (isReceiptFamily(child)) continue;
            if (continuations.has(cid)) continue;
            if (!rowIsCanceled(child)) out.push(child);
            walk(cid);
        }
    };
    walk(startId);
    return out;
};

/**
 * The open (not fully paid) TaxInvoices of the row's group with the row's customer, oldest first —
 * what one receipt can pay together. [] unless the row is one of them and there are at least two.
 */
export const getGroupedOpenInvoices = (row, allRows = []) => {
    const id = rowId(row);
    if (!id || rowType(row) !== TAX_INVOICE_TYPE) return [];
    const { parentOf, childrenOf, byId } = buildConversionForest(allRows);
    const groupIds = [];
    const seen = new Set();
    const stack = [findConversionRootId(id, parentOf)];
    while (stack.length > 0) {
        const current = stack.pop();
        if (seen.has(current)) continue;
        seen.add(current);
        groupIds.push(current);
        stack.push(...(childrenOf.get(current) || []));
    }
    const customer = String(row.accountCodeId ?? row.AccountCodeId ?? '');
    const invoices = groupIds
        .map((gid) => byId.get(gid))
        .filter((r) => r
            && rowType(r) === TAX_INVOICE_TYPE
            && !rowIsCanceled(r)
            && getProcessingStatus(r) !== LIFECYCLE_STATUS.CLOSED
            && String(r.accountCodeId ?? r.AccountCodeId ?? '') === customer);
    if (invoices.length < 2 || !invoices.some((r) => rowId(r) === id)) return [];
    const time = (r) => new Date(r.documentDate ?? r.DocumentDate ?? 0).getTime() || 0;
    return invoices.sort((a, b) => time(a) - time(b)
        || String(a.reference ?? '').localeCompare(String(b.reference ?? ''), undefined, { numeric: true }));
};

export const isCancelLikeAction = (actionValue, documentType) => {
    if (!actionValue) return false;
    if (String(actionValue).startsWith('cancel_')) return true;
    return actionValue === 'generate_credit_invoice' && Number(documentType) === TAX_INVOICE_TYPE;
};

/**
 * "שיוך מסמכים" helpers. The backend (Documents/lifecycle/link-options) decides which documents can
 * be linked and how much; these helpers only search, pick and pre-validate what it returned. The
 * link endpoint validates everything again.
 */
export const RELATION_TYPE = Object.freeze({
    GENERATED_FROM: 1,
    PAYMENT_FOR: 2,
});

/** DocumentEntityType, matching the backend enum. */
export const ENTITY_TYPE = Object.freeze({
    UNKNOWN: 0,
    TRANSACTION_DOCUMENT: 1,
    INCOME: 2,
    PAYMENT_ACCEPTANCE: 3,
});

export const LINK_DIRECTION = Object.freeze({
    /** The anchor is the source; candidates are invoices / receipts. */
    AS_SOURCE: 0,
    /** The anchor is the target; candidates are its possible sources. */
    AS_TARGET: 1,
});

const AMOUNT_TOLERANCE = 0.01;

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal];

export const normalizeItemMatchStatus = (value) => {
    if (value == null || value === '') return ITEM_MATCH_STATUS.UNKNOWN;
    if (typeof value === 'number' && value >= 0 && value <= 5) return value;
    const names = {
        Unknown: 0, Mismatch: 1, AmountOnlyMatch: 2, PartialMatch: 3, ExactMatch: 4, AmountMismatch: 5,
        unknown: 0, mismatch: 1, amountOnlyMatch: 2, partialMatch: 3, exactMatch: 4, amountMismatch: 5,
    };
    return names[value] ?? ITEM_MATCH_STATUS.UNKNOWN;
};

export const itemMatchLabelKey = (status) => {
    switch (Number(status)) {
        case ITEM_MATCH_STATUS.EXACT: return 'LinkDocumentsExactMatch';
        case ITEM_MATCH_STATUS.PARTIAL: return 'LinkDocumentsPartialMatch';
        case ITEM_MATCH_STATUS.AMOUNT_MISMATCH: return 'LinkDocumentsAmountMismatch';
        case ITEM_MATCH_STATUS.AMOUNT_ONLY: return 'LinkDocumentsAmountOnlyMatch';
        case ITEM_MATCH_STATUS.MISMATCH: return 'LinkDocumentsItemMismatch';
        default: return 'LinkDocumentsItemUnknown';
    }
};

const normalizeItemText = (value) => {
    if (value == null || String(value).trim() === '') return null;
    return String(value).trim().toUpperCase().replace(/\s+/g, ' ');
};

const aliasesOf = (line) => {
    const aliases = [];
    const id = line?.itemId || line?.ItemId;
    if (id && String(id) !== '00000000-0000-0000-0000-000000000000') aliases.push(`id:${String(id).toLowerCase().replace(/-/g, '')}`);
    const makat = normalizeItemText(line?.makat ?? line?.Makat);
    if (makat) aliases.push(`m:${makat}`);
    const name = normalizeItemText(line?.name ?? line?.Name);
    if (name) aliases.push(`n:${name}`);
    return aliases;
};

const itemIdentity = (line) => aliasesOf(line)[0] || null;

const compatibleItemBags = (left, right) =>
    left.aliases.some((a) => right.aliases.includes(a));

/** Aggregate compatible duplicate lines (same makat / item id / name). Price is not part of identity. */
export const normalizeItemBag = (lines = []) => {
    const bag = [];
    for (const line of lines) {
        const aliases = aliasesOf(line);
        if (!aliases.length) return null;
        const next = {
            aliases,
            quantity: Number(line?.quantity ?? line?.Quantity) || 0,
        };
        const existing = bag.find((entry) => compatibleItemBags(entry, next));
        if (existing) {
            existing.quantity += next.quantity;
            next.aliases.forEach((a) => {
                if (!existing.aliases.includes(a)) existing.aliases.push(a);
            });
        } else {
            bag.push(next);
        }
    }
    return bag.length ? bag : null;
};

/**
 * Mirrors DocumentItemMatch.Compare. Used for create-mode revalidation and the expandable
 * comparison table; ranking / IsExactMatch on existing docs come from the server.
 * @param {string} [mode] ITEM_MATCH_MODE value; default QUANTITY_AMOUNT_AND_ITEMS.
 */
export const compareDocumentItemLines = (
    sourceLines,
    targetLines,
    amountMatches,
    mode = ITEM_MATCH_MODE.QUANTITY_AMOUNT_AND_ITEMS,
) => {
    if (!sourceLines?.length || !targetLines?.length) return ITEM_MATCH_STATUS.UNKNOWN;
    const source = normalizeItemBag(sourceLines);
    const target = normalizeItemBag(targetLines);
    if (!source || !target) return ITEM_MATCH_STATUS.UNKNOWN;
    const unused = target.map(() => true);
    const bagsEqual = source.length === target.length && source.every((left) => {
        const index = target.findIndex((right, i) => unused[i] && compatibleItemBags(left, right));
        if (index < 0 || Math.abs(left.quantity - target[index].quantity) > 0.001) return false;
        unused[index] = false;
        return true;
    });
    const candidateSubset = target.every((taken) => {
        const match = source.find((left) => compatibleItemBags(left, taken));
        return !!match && taken.quantity <= match.quantity + 0.001;
    });
    const overlaps = source.some((left) => target.some((right) => compatibleItemBags(left, right)));

    if (mode === ITEM_MATCH_MODE.QUANTITY_AND_ITEMS) {
        if (bagsEqual) return ITEM_MATCH_STATUS.EXACT;
        if (candidateSubset && !bagsEqual) return ITEM_MATCH_STATUS.PARTIAL;
        if (overlaps && !candidateSubset) return ITEM_MATCH_STATUS.MISMATCH;
        if (amountMatches) return ITEM_MATCH_STATUS.AMOUNT_ONLY;
        return ITEM_MATCH_STATUS.MISMATCH;
    }

    if (bagsEqual && amountMatches) return ITEM_MATCH_STATUS.EXACT;
    if (candidateSubset && !bagsEqual) return ITEM_MATCH_STATUS.PARTIAL;
    if (bagsEqual) return ITEM_MATCH_STATUS.AMOUNT_MISMATCH;
    if (overlaps && !candidateSubset) return ITEM_MATCH_STATUS.MISMATCH;
    if (amountMatches) return ITEM_MATCH_STATUS.AMOUNT_ONLY;
    return ITEM_MATCH_STATUS.MISMATCH;
};

/**
 * Receipt ↔ invoices as a whole: the selected documents' lines together against the anchor's.
 * 'match' / 'mismatch', or null when a side has no product lines (receipts often carry payment
 * methods only), in which case the amounts alone decide.
 */
export const comparePaymentSelectionItems = (anchorLines, selectedLines = []) => {
    if (!anchorLines?.length || !selectedLines.length || selectedLines.some((lines) => !lines?.length)) return null;
    const status = compareDocumentItemLines(
        selectedLines.flat(), anchorLines, true, ITEM_MATCH_MODE.AMOUNT_AND_ITEMS);
    if (status === ITEM_MATCH_STATUS.UNKNOWN) return null;
    return status === ITEM_MATCH_STATUS.EXACT ? 'match' : 'mismatch';
};

export const snapshotLineTotal = (item) => {
    const price = Number(item?.price ?? item?.Price) || 0;
    const quantity = Number(item?.quantity ?? item?.Quantity) || 0;
    const discount = Number(item?.discount ?? item?.Discount) || 0;
    return (price * quantity) - ((price * quantity * discount) / 100);
};

export const compareItemRows = (sourceLines = [], targetLines = []) => {
    const sourceBag = new Map();
    const targetBag = new Map();
    const push = (bag, line) => {
        const makat = (line?.makat || line?.Makat || '').trim();
        const name = (line?.name || line?.Name || '').trim();
        const identity = itemIdentity(line);
        const displayKey = normalizeItemText(makat) || normalizeItemText(name) || identity || 'u';
        if (!bag.has(displayKey)) bag.set(displayKey, { key: displayKey, makat, name: name || identity || '', quantity: 0 });
        bag.get(displayKey).quantity += Number(line?.quantity ?? line?.Quantity) || 0;
        if (!bag.get(displayKey).makat && makat) bag.get(displayKey).makat = makat;
        if (!bag.get(displayKey).name && name) bag.get(displayKey).name = name;
    };
    sourceLines.forEach((l) => push(sourceBag, l));
    targetLines.forEach((l) => push(targetBag, l));
    const keys = new Set([...sourceBag.keys(), ...targetBag.keys()]);
    return [...keys].map((key) => {
        const sourceQty = sourceBag.get(key)?.quantity || 0;
        const targetQty = targetBag.get(key)?.quantity || 0;
        const makat = sourceBag.get(key)?.makat || targetBag.get(key)?.makat || '';
        const name = sourceBag.get(key)?.name || targetBag.get(key)?.name || '';
        let result = 'mismatch';
        if (sourceQty > 0 && targetQty > 0 && Math.abs(sourceQty - targetQty) <= 0.001) result = 'match';
        else if (sourceQty > 0 && targetQty > 0) result = 'qtyDiff';
        else if (sourceQty > 0) result = 'notIncluded';
        else result = 'different';
        return { key, makat, name, label: name, sourceQty, targetQty, result };
    });
};

export const roundAmount = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const formatLinkAmount = (value) => {
    if (value == null || value === '' || Number.isNaN(Number(value))) return '-';
    return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Generate actions that invoice a source document and so consume its remaining items. */
export const INVOICING_GENERATE_ACTIONS = new Set(['generate_invoice', 'generate_invoice_receipt']);
/** Quotation, Order, Transaction, DeliveryNote. */
export const REMAINING_ITEMS_SOURCE_TYPES = new Set([1, 2, 3, 11]);
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const lineIdentityText = (value) => {
    const text = String(value ?? '').trim().replace(/\s+/g, ' ').toUpperCase();
    return text || null;
};

/** Same line identity as the server's DocumentItemMatch: catalogue item id, then makat, then name. */
export const isSameDocumentLine = (a, b) => {
    const idA = a?.itemId && a.itemId !== EMPTY_GUID ? String(a.itemId) : null;
    const idB = b?.itemId && b.itemId !== EMPTY_GUID ? String(b.itemId) : null;
    if (idA && idA === idB) return true;
    const makatA = lineIdentityText(a?.makat);
    if (makatA && makatA === lineIdentityText(b?.makat)) return true;
    const nameA = lineIdentityText(a?.name);
    return !!nameA && nameA === lineIdentityText(b?.name);
};

/**
 * Pairs each source line with its line from `remaining-items` (issue order, first unused match);
 * null where nothing matches.
 */
export const pairRemainingLines = (items, remainingLines) => {
    const used = new Set();
    return (items || []).map((item) => {
        const index = (remainingLines || []).findIndex((line, i) => !used.has(i) && isSameDocumentLine(item, line));
        if (index < 0) return null;
        used.add(index);
        return remainingLines[index];
    });
};

/**
 * What is still free on a grid / API document for conversion or another receipt.
 * Prefers lifecycle RemainingAmount; falls back to original − applied, then amount.
 */
export const getDocumentAvailableAmount = (doc) => {
    if (!doc) return 0;
    const remaining = doc.remainingAmount ?? doc.RemainingAmount;
    if (remaining != null && !Number.isNaN(Number(remaining))) {
        return roundAmount(Math.max(0, Number(remaining)));
    }
    const original = doc.originalAmount ?? doc.OriginalAmount;
    const applied = doc.appliedAmount ?? doc.AppliedAmount;
    if (original != null && applied != null
        && !Number.isNaN(Number(original)) && !Number.isNaN(Number(applied))) {
        return roundAmount(Math.max(0, Number(original) - Number(applied)));
    }
    const amount = doc.amount ?? doc.Amount;
    return amount != null && !Number.isNaN(Number(amount))
        ? roundAmount(Math.max(0, Number(amount)))
        : 0;
};

/**
 * Projection for Generate Receipt from a (possibly partly paid) TaxInvoice:
 * available / amount to collect / remaining after / invoice status after.
 */
export const projectGenerateAllocation = (availableAmount, linkAmount) => {
    const available = roundAmount(availableAmount);
    const total = roundAmount(linkAmount);
    const remainingAfter = roundAmount(Math.max(0, available - total));
    return {
        available,
        total,
        remainingAfter,
        sourceStatus: remainingAfter <= AMOUNT_TOLERANCE ? LIFECYCLE_STATUS.CLOSED : LIFECYCLE_STATUS.PARTIAL,
    };
};

/** dd/MM/yyyy, matching the grid. */
export const formatLinkDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${date.getFullYear()}`;
};

/** Normalizes one LinkableDocumentInfo (camelCase or PascalCase) from the API. */
export const normalizeLinkDocument = (doc) => (doc ? {
    documentId: pick(doc, 'documentId', 'DocumentId'),
    entityType: pick(doc, 'entityType', 'EntityType'),
    documentType: pick(doc, 'documentType', 'DocumentType'),
    reference: pick(doc, 'reference', 'Reference') ?? '',
    documentDate: pick(doc, 'documentDate', 'DocumentDate'),
    accountCodeId: pick(doc, 'accountCodeId', 'AccountCodeId'),
    accountCodeName: pick(doc, 'accountCodeName', 'AccountCodeName') ?? '',
    amount: pick(doc, 'amount', 'Amount'),
    currency: pick(doc, 'currency', 'Currency'),
    isValid: pick(doc, 'isValid', 'IsValid') !== false,
    processingStatus: pick(doc, 'processingStatus', 'ProcessingStatus'),
    manualStatus: pick(doc, 'manualStatus', 'ManualStatus'),
    appliedAmount: pick(doc, 'appliedAmount', 'AppliedAmount'),
    remainingAmount: pick(doc, 'remainingAmount', 'RemainingAmount'),
    availableAmount: pick(doc, 'availableAmount', 'AvailableAmount'),
    suggestedAmount: pick(doc, 'suggestedAmount', 'SuggestedAmount'),
    isExactMatch: !!pick(doc, 'isExactMatch', 'IsExactMatch'),
    itemMatchStatus: normalizeItemMatchStatus(pick(doc, 'itemMatchStatus', 'ItemMatchStatus')),
} : null);

/** Normalizes the whole DocumentLinkOptions response. */
export const normalizeLinkOptions = (options) => {
    if (!options) return null;
    const anchors = (pick(options, 'anchors', 'Anchors') || []).map((a) => ({
        documentId: pick(a, 'documentId', 'DocumentId'),
        direction: pick(a, 'direction', 'Direction'),
        relationType: pick(a, 'relationType', 'RelationType'),
        availableAmount: pick(a, 'availableAmount', 'AvailableAmount'),
        candidates: (pick(a, 'candidates', 'Candidates') || []).map(normalizeLinkDocument),
    }));
    return {
        document: normalizeLinkDocument(pick(options, 'document', 'Document')),
        chainDocuments: (pick(options, 'chainDocuments', 'ChainDocuments') || []).map(normalizeLinkDocument),
        chainLinks: (pick(options, 'chainLinks', 'ChainLinks') || []).map((l) => ({
            relationshipId: pick(l, 'relationshipId', 'RelationshipId'),
            sourceDocumentId: pick(l, 'sourceDocumentId', 'SourceDocumentId'),
            targetDocumentId: pick(l, 'targetDocumentId', 'TargetDocumentId'),
            relationType: pick(l, 'relationType', 'RelationType'),
            appliedAmount: pick(l, 'appliedAmount', 'AppliedAmount'),
        })),
        anchors,
        pairStatus: pick(options, 'pairStatus', 'PairStatus') ?? null,
        pairMessage: pick(options, 'pairMessage', 'PairMessage') ?? '',
    };
};

/** ManualLinkStatus values, matching the backend enum (sent as numbers in link-options). */
const MANUAL_LINK_STATUS_CODES = Object.freeze([
    'Linked', 'NotFound', 'SelfLink', 'InvalidDocument', 'IncompatibleTypes', 'DifferentCustomer',
    'AlreadyLinked', 'Cycle', 'CurrencyMismatch', 'FullyAllocated', 'OverAllocated', 'InvalidAmount',
    'UnknownAllocation', 'ClosedDocument', 'TargetFullyAllocated',
]);

/** Name of a ManualLinkStatus given as a number or a string, or null. */
export const linkStatusCode = (status) => {
    if (status == null || status === '') return null;
    if (typeof status === 'number') return MANUAL_LINK_STATUS_CODES[status] ?? null;
    return MANUAL_LINK_STATUS_CODES.includes(status) ? status : null;
};

export const anchorKey = (anchor) => (anchor ? `${anchor.documentId}:${anchor.direction}:${anchor.relationType}` : '');

/**
 * Finds the anchor / candidate pair for two selected grid rows, whichever was selected first.
 * Returns null when the backend does not allow linking them.
 */
export const findLinkPair = (options, firstId, secondId) => {
    if (!options || firstId == null || secondId == null) return null;
    const same = (a, b) => String(a) === String(b);
    for (const anchor of options.anchors || []) {
        const anchorIsFirst = same(anchor.documentId, firstId);
        const anchorIsSecond = same(anchor.documentId, secondId);
        if (!anchorIsFirst && !anchorIsSecond) continue;
        const otherId = anchorIsFirst ? secondId : firstId;
        const candidate = anchor.candidates.find((c) => same(c.documentId, otherId));
        if (candidate) return { anchor, candidate };
    }
    return null;
};

/** Free-text search over reference, amount, date, customer and type label. */
export const filterLinkCandidates = (candidates = [], query = '', getTypeLabel = () => '') => {
    const text = String(query || '').trim().toLowerCase();
    if (!text) return candidates;
    const numeric = Number(text.replace(/,/g, ''));
    return candidates.filter((c) => {
        if (!Number.isNaN(numeric) && text !== '' &&
            [c.amount, c.availableAmount].some((v) => v != null && Math.abs(Number(v) - numeric) < AMOUNT_TOLERANCE)) {
            return true;
        }
        const haystack = [
            c.reference,
            c.accountCodeName,
            formatLinkDate(c.documentDate),
            formatLinkAmount(c.amount),
            String(c.amount ?? ''),
            getTypeLabel(c.documentType) || '',
        ].join(' ').toLowerCase();
        return haystack.includes(text);
    });
};

/** Source / target sides of a proposed link. */
export const resolveLinkSides = (anchor, candidate) => {
    if (!anchor || !candidate) return null;
    const anchorIsSource = anchor.direction === LINK_DIRECTION.AS_SOURCE;
    return {
        sourceId: anchorIsSource ? anchor.documentId : candidate.documentId,
        targetId: anchorIsSource ? candidate.documentId : anchor.documentId,
        sourceRemaining: roundAmount(anchorIsSource ? anchor.availableAmount : candidate.availableAmount),
        targetAvailable: roundAmount(anchorIsSource ? candidate.availableAmount : anchor.availableAmount),
    };
};

/** Translation key of the first problem with the amount, or null when it can be sent. */
export const validateLinkAmount = (amount, anchor, candidate) => {
    const sides = resolveLinkSides(anchor, candidate);
    if (!sides) return 'LinkDocumentsSelectCandidate';
    if (amount === '' || amount == null || Number.isNaN(Number(amount))) return 'LinkDocumentsAmountRequired';
    const value = roundAmount(amount);
    if (value <= 0) return 'LinkDocumentsAmountPositive';
    if (value > sides.sourceRemaining + AMOUNT_TOLERANCE) return 'LinkDocumentsAmountExceedsSource';
    if (value > sides.targetAvailable + AMOUNT_TOLERANCE) return 'LinkDocumentsAmountExceedsTarget';
    return null;
};

const isPayment = (anchor) => Number(anchor?.relationType) === RELATION_TYPE.PAYMENT_FOR;

/** Quotation, Order, Transaction, DeliveryNote. */
const LINK_SOURCE_TYPES = new Set([1, 2, 3, 11]);
const isSourceType = (type) => type != null && LINK_SOURCE_TYPES.has(Number(type));

const coveredStatus = (remainingAfter) =>
    (remainingAfter <= AMOUNT_TOLERANCE ? LIFECYCLE_STATUS.CLOSED : LIFECYCLE_STATUS.PARTIAL);

/**
 * What the link would leave behind. The source status is what the backend calculates for a
 * regular business; an ExemptDealer receipt closes its source in full regardless of amount.
 * A TaxInvoice paid by a receipt stays PartiallyProcessed while Remaining &gt; 0 and becomes
 * Closed when covered; the receipt itself is always Closed (terminal). Quotation closes once
 * any document is generated/linked from it. Other source→source links (Order → DeliveryNote)
 * keep status until invoiced (sourceStatus null).
 * anchorType: document type of the anchor (needed when the anchor is the target, and for
 * Quotation AS_SOURCE when anchor.documentType is omitted).
 */
export const projectLink = (amount, anchor, candidate, anchorType = null) => {
    const sides = resolveLinkSides(anchor, candidate);
    if (!sides) return null;
    const value = roundAmount(amount);
    const sourceRemainingAfter = roundAmount(Math.max(0, sides.sourceRemaining - value));
    const payment = isPayment(anchor);
    const targetType = anchor.direction === LINK_DIRECTION.AS_SOURCE ? candidate.documentType : anchorType;
    const sourceType = anchor.direction === LINK_DIRECTION.AS_SOURCE
        ? (anchor.documentType ?? anchorType)
        : candidate.documentType;
    let sourceStatus = coveredStatus(sourceRemainingAfter);
    if (!payment && Number(sourceType) === QUOTATION_TYPE) {
        sourceStatus = LIFECYCLE_STATUS.CLOSED;
    } else if (!payment && isSourceType(targetType)) {
        sourceStatus = null;
    }
    return {
        ...sides,
        amount: value,
        sourceRemainingAfter,
        targetAvailableAfter: roundAmount(Math.max(0, sides.targetAvailable - value)),
        sourceStatus,
        targetStatus: payment ? LIFECYCLE_STATUS.CLOSED : null,
    };
};

export const buildLinkRequest = (caseId, anchor, candidate, amount) => {
    const sides = resolveLinkSides(anchor, candidate);
    if (!sides) return null;
    return {
        caseId,
        sourceDocumentId: sides.sourceId,
        targetDocumentId: sides.targetId,
        appliedAmount: roundAmount(amount),
    };
};

const sumSelected = (selections, exceptId = null) => roundAmount(
    Object.entries(selections || {})
        .filter(([id]) => exceptId == null || String(id) !== String(exceptId))
        .reduce((total, [, amount]) => total + (Number(amount) || 0), 0)
);

/** What the anchor still has free after the selected amounts (never below zero). */
export const remainingForSelections = (anchor, selections = {}, exceptId = null) => {
    if (!anchor) return 0;
    return roundAmount(Math.max(0, roundAmount(anchor.availableAmount) - sumSelected(selections, exceptId)));
};

/** True when another candidate can still be selected: the selected amounts do not cover the anchor yet. */
export const canSelectMore = (anchor, selections = {}) =>
    remainingForSelections(anchor, selections) > AMOUNT_TOLERANCE;

/**
 * A receipt and the invoices it pays must match, so payment links are whole on both sides: a
 * candidate bigger than what the anchor has free can never be linked.
 */
export const exceedsPaymentAnchor = (anchor, candidate) =>
    isPayment(anchor)
    && roundAmount(candidate?.availableAmount) > roundAmount(anchor?.availableAmount) + AMOUNT_TOLERANCE;

/** A payment candidate that no longer fits whole next to the already selected ones. */
export const isPaymentCandidateTooLarge = (anchor, candidate, selections = {}) => {
    if (!isPayment(anchor) || !candidate) return false;
    if (Object.prototype.hasOwnProperty.call(selections || {}, String(candidate.documentId))) return false;
    return roundAmount(candidate.availableAmount) > remainingForSelections(anchor, selections) + AMOUNT_TOLERANCE;
};

/** GeneratedFrom unknown/mismatch cannot be chosen. PaymentFor is checked by amount (exceedsPaymentAnchor). */
export const isLinkCandidateBlocked = (candidate, relationType) => {
    if (Number(relationType) === RELATION_TYPE.PAYMENT_FOR) return false;
    const status = Number(candidate?.itemMatchStatus ?? ITEM_MATCH_STATUS.UNKNOWN);
    return status === ITEM_MATCH_STATUS.UNKNOWN || status === ITEM_MATCH_STATUS.MISMATCH;
};

export const isExactLinkCandidate = (candidate, relationType) => {
    if (Number(relationType) === RELATION_TYPE.PAYMENT_FOR) return !!candidate?.isExactMatch;
    return Number(candidate?.itemMatchStatus) === ITEM_MATCH_STATUS.EXACT;
};

/**
 * Create-mode default: tick every ExactMatch row, filling the new document's remaining amount
 * the same way the grid pre-ticks a selected pair.
 */
export const selectionsFromExactMatches = (anchor) => {
    const selections = {};
    if (!anchor) return selections;
    (anchor.candidates || []).forEach((candidate) => {
        if (!isExactLinkCandidate(candidate, anchor.relationType)) return;
        if (!canSelectMore(anchor, selections)) return;
        if (isPaymentCandidateTooLarge(anchor, candidate, selections)) return;
        const amount = defaultSelectionAmount(anchor, candidate, selections);
        if (amount > 0) selections[String(candidate.documentId)] = amount;
    });
    return selections;
};

/**
 * Default amount for a newly selected candidate: what it can take, limited to what the anchor has
 * left after the other selected candidates.
 */
export const defaultSelectionAmount = (anchor, candidate, selections = {}) => {
    if (!anchor || !candidate) return 0;
    const anchorLeft = remainingForSelections(anchor, selections, candidate.documentId);
    return roundAmount(Math.max(0, Math.min(roundAmount(candidate.availableAmount), anchorLeft)));
};

/**
 * Several candidates linked to one anchor (e.g. one invoice paid by three receipts).
 * selections: { [candidateId]: amount }. Each amount is checked against its own pair, and the total
 * against what the anchor has available. A payment link must match on both sides: every selected
 * document is taken whole and together they equal the anchor's available amount.
 */
export const validateLinkSelections = (anchor, selections = {}, candidates = []) => {
    const byId = new Map(candidates.map((c) => [String(c.documentId), c]));
    const ids = Object.keys(selections || {});
    const payment = isPayment(anchor);
    const rowErrors = {};
    ids.forEach((id) => {
        const candidate = byId.get(String(id));
        let error = validateLinkAmount(selections[id], anchor, candidate);
        if (!error && payment && candidate
            && Math.abs(roundAmount(selections[id]) - roundAmount(candidate.availableAmount)) > AMOUNT_TOLERANCE) {
            error = 'LinkDocumentsPaymentMustBeWhole';
        }
        if (error) rowErrors[id] = error;
    });
    const total = sumSelected(selections);
    let totalError = null;
    let totalErrorParams = null;
    if (ids.length === 0) totalError = 'LinkDocumentsSelectCandidate';
    else if (anchor && total > roundAmount(anchor.availableAmount) + AMOUNT_TOLERANCE) totalError = 'LinkDocumentsTotalExceedsAvailable';
    else if (payment && Math.abs(total - roundAmount(anchor.availableAmount)) > AMOUNT_TOLERANCE) {
        totalError = 'LinkDocumentsPaymentTotalMismatch';
        totalErrorParams = { available: formatLinkAmount(anchor.availableAmount), total: formatLinkAmount(total) };
    }
    return { rowErrors, totalError, totalErrorParams, total, isValid: !totalError && Object.keys(rowErrors).length === 0 };
};

/**
 * What the selected links would leave behind: the anchor's free amount and resulting status.
 * As the source (TaxInvoice → receipts) it becomes Closed only when selections cover Remaining;
 * a receipt anchor is always Closed once linked. targetStatus is Closed for the selected receipts.
 */
export const projectLinkSelections = (anchor, selections = {}, candidates = []) => {
    if (!anchor) return null;
    const total = sumSelected(selections);
    const anchorRemainingAfter = roundAmount(Math.max(0, roundAmount(anchor.availableAmount) - total));
    const payment = isPayment(anchor);
    const byId = new Map(candidates.map((c) => [String(c.documentId), c]));
    const toLaterSource = Object.keys(selections || {}).some((id) => isSourceType(byId.get(String(id))?.documentType));
    const isQuotationAnchor = Number(anchor.documentType) === QUOTATION_TYPE;
    let anchorStatus = null;
    if (anchor.direction === LINK_DIRECTION.AS_SOURCE) {
        if (isQuotationAnchor && total > AMOUNT_TOLERANCE) {
            anchorStatus = LIFECYCLE_STATUS.CLOSED;
        } else if (!toLaterSource) {
            anchorStatus = coveredStatus(anchorRemainingAfter);
        }
    } else if (payment) {
        anchorStatus = total > AMOUNT_TOLERANCE ? LIFECYCLE_STATUS.CLOSED : null;
    }
    return {
        total,
        anchorRemainingAfter,
        anchorStatus,
        targetStatus: payment && anchor.direction === LINK_DIRECTION.AS_SOURCE ? LIFECYCLE_STATUS.CLOSED : null,
    };
};

export const buildLinkManyRequest = (caseId, anchor, selections = {}, candidates = []) => {
    const byId = new Map(candidates.map((c) => [String(c.documentId), c]));
    const links = Object.entries(selections || {})
        .map(([id, amount]) => buildLinkRequest(caseId, anchor, byId.get(String(id)), amount))
        .filter(Boolean)
        .map(({ sourceDocumentId, targetDocumentId, appliedAmount }) => ({ sourceDocumentId, targetDocumentId, appliedAmount }));
    return { caseId, links };
};

/**
 * Selections for the grid rows picked before opening the dialog: the anchor that offers the most of
 * them, with each one's default amount. null when none of them can be linked.
 */
export const findLinkSelections = (options, primaryId, otherIds = []) => {
    if (!options || primaryId == null || otherIds.length === 0) return null;
    const wanted = otherIds.map(String);
    let best = null;
    for (const anchor of options.anchors || []) {
        if (String(anchor.documentId) !== String(primaryId)) continue;
        const matches = anchor.candidates.filter((c) => wanted.includes(String(c.documentId)));
        if (matches.length > 0 && (!best || matches.length > best.matches.length)) best = { anchor, matches };
    }
    if (!best) {
        const single = otherIds.length === 1 ? findLinkPair(options, primaryId, otherIds[0]) : null;
        if (!single) return null;
        best = { anchor: single.anchor, matches: [single.candidate] };
    }
    const selections = {};
    best.matches
        .slice()
        .sort((a, b) => wanted.indexOf(String(a.documentId)) - wanted.indexOf(String(b.documentId)))
        .forEach((c) => {
            if (isPaymentCandidateTooLarge(best.anchor, c, selections)) return;
            selections[String(c.documentId)] = defaultSelectionAmount(best.anchor, c, selections);
        });
    Object.keys(selections).forEach((id) => { if (!(selections[id] > 0)) delete selections[id]; });
    return { anchor: best.anchor, selections, missing: otherIds.length - Object.keys(selections).length };
};

/**
 * One receipt for several TaxInvoices. `invoiceOptions`: the normalized link options of each invoice.
 * A receipt qualifies when every invoice offers it and its free amount equals the invoices' open
 * total. `closed`: invoices with nothing left to pay (no payment anchor).
 */
export const findGroupReceiptMatches = (invoiceOptions = []) => {
    const invoices = [];
    const closed = [];
    const offers = [];
    invoiceOptions.forEach((options) => {
        const doc = options?.document;
        if (!doc) return;
        const anchor = (options.anchors || []).find((a) =>
            isPayment(a) && Number(a.direction) === LINK_DIRECTION.AS_SOURCE);
        if (!anchor) {
            closed.push(doc);
            return;
        }
        invoices.push({ ...doc, availableAmount: anchor.availableAmount });
        offers.push(new Map(anchor.candidates.map((c) => [String(c.documentId), c])));
    });
    const total = roundAmount(invoices.reduce((sum, inv) => sum + roundAmount(inv.availableAmount), 0));
    const [first, ...rest] = offers;
    const common = first
        ? [...first.values()].filter((c) => rest.every((offer) => offer.has(String(c.documentId))))
        : [];
    const receipts = closed.length
        ? []
        : common.filter((c) => Math.abs(roundAmount(c.availableAmount) - total) <= AMOUNT_TOLERANCE);
    return { invoices, closed, total, receipts, otherCount: common.length - receipts.length };
};

const PAYMENT_TYPES = new Set([6, 7]);
const SOURCE_TYPES = new Set([1, 2, 3, 11]);
const typeGroup = (type) => {
    const value = Number(type);
    if (SOURCE_TYPES.has(value)) return 'source';
    if (PAYMENT_TYPES.has(value)) return 'payment';
    return `type-${value}`;
};

/**
 * Which of the selected grid rows the dialog opens on: the one row whose kind differs from all the
 * others (the invoice among its receipts or orders), else the first selected.
 */
export const pickLinkPrimary = (rows = []) => {
    if (rows.length <= 2) return rows[0]?.id ?? null;
    const groups = new Map();
    rows.forEach((row) => {
        const key = typeGroup(row?.transactionDocumentType);
        groups.set(key, [...(groups.get(key) || []), row]);
    });
    if (groups.size === 2) {
        const single = [...groups.values()].find((group) => group.length === 1);
        if (single) return single[0].id;
    }
    return rows[0]?.id ?? null;
};

/**
 * Pending links for a document being created ("שיוך מסמכים", create mode). The new document has no
 * id yet; these are the existing documents chosen as its sources / paid invoices, kept in the form
 * until the document is generated. The anchor in create mode is always the new document (AsTarget),
 * so every selected candidate becomes a source of it.
 */
export const buildPendingLinks = (anchor, selections = {}, candidates = []) => {
    if (!anchor) return [];
    const byId = new Map(candidates.map((c) => [String(c.documentId), c]));
    return Object.entries(selections || {})
        .map(([id, amount]) => {
            const candidate = byId.get(String(id));
            if (!candidate) return null;
            return {
                documentId: candidate.documentId,
                entityType: candidate.entityType,
                documentType: candidate.documentType,
                reference: candidate.reference,
                accountCodeId: candidate.accountCodeId,
                accountCodeName: candidate.accountCodeName,
                documentDate: candidate.documentDate,
                amount: candidate.amount,
                availableAmount: candidate.availableAmount,
                processingStatus: candidate.processingStatus,
                isExactMatch: !!candidate.isExactMatch,
                itemMatchStatus: candidate.itemMatchStatus,
                items: Array.isArray(candidate.items) ? candidate.items : [],
                appliedAmount: roundAmount(amount),
                relationType: anchor.relationType,
            };
        })
        .filter((link) => link && link.appliedAmount > 0);
};

/** Form invoice/receipt lines as the draft snapshot sent to draft-link-options. */
const isGuid = (value) =>
    typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const toLinkDraftItems = (invoiceItems = []) =>
    (invoiceItems || []).map((item) => {
        const rawId = item.itemId || item.id || null;
        return {
            itemId: isGuid(rawId) ? rawId : null,
            makat: item.makat || '',
            name: item.name || '',
            quantity: Number(item.quantity) || 0,
            price: Number(item.price) || 0,
            discount: Number(item.discount) || 0,
            includeVat: item.includeVat !== false,
        };
    });

/** Re-run item matching when the document being created changes its lines or amount. */
export const revalidatePendingLinkMatches = (
    pendingLinks = [],
    draftItems = [],
    draftAmount = 0,
    draftDocumentType = null,
) =>
    (pendingLinks || []).map((link) => {
        const amountMatches = Math.abs(Number(link.availableAmount ?? link.amount) - Number(draftAmount || 0))
            <= AMOUNT_TOLERANCE;
        const mode = resolveItemMatchMode(link.relationType, draftDocumentType);
        // Receipts without product lines: keep amount-only ExactMatch from the server.
        if (mode === ITEM_MATCH_MODE.AMOUNT_AND_ITEMS
            && (!(draftItems || []).length || !(link.items || []).length)) {
            return link;
        }
        // Create-mode is always AsTarget: compare source (link.items) → draft so a fuller
        // Quotation with extra lines is PartialMatch when the draft items are a subset.
        const status = compareDocumentItemLines(link.items || [], draftItems || [], amountMatches, mode);
        if (status === link.itemMatchStatus && (status === ITEM_MATCH_STATUS.EXACT) === !!link.isExactMatch) {
            return link;
        }
        return { ...link, itemMatchStatus: status, isExactMatch: status === ITEM_MATCH_STATUS.EXACT };
    });

/** { [documentId]: appliedAmount } for pre-selecting the modal from existing pending links. */
export const pendingLinksToSelections = (pendingLinks = []) => {
    const selections = {};
    (pendingLinks || []).forEach((link) => {
        if (link && link.documentId != null) selections[String(link.documentId)] = link.appliedAmount;
    });
    return selections;
};

export const pendingLinksTotal = (pendingLinks = []) =>
    roundAmount((pendingLinks || []).reduce((sum, link) => sum + (Number(link?.appliedAmount) || 0), 0));

/**
 * The new document may not allocate to its sources / invoices more than its own amount. Returns the
 * total and whether it is over-allocated (null amount = not yet known, nothing to flag).
 */
export const validatePendingLinks = (pendingLinks = [], documentAmount = null) => {
    const total = pendingLinksTotal(pendingLinks);
    const amount = documentAmount == null || documentAmount === '' ? null : roundAmount(documentAmount);
    const overAllocated = amount != null && total > amount + AMOUNT_TOLERANCE;
    return { total, documentAmount: amount, overAllocated, isValid: (pendingLinks || []).length === 0 || !overAllocated };
};

/**
 * Drops pending links that no longer belong to the current customer. Changing the customer must
 * never carry another customer's links into the new document (validation rule 13 / 27).
 */
export const pendingLinksForCustomer = (pendingLinks = [], customerAccountCodeId = null) => {
    if (customerAccountCodeId == null || customerAccountCodeId === '') return [];
    const wanted = String(customerAccountCodeId).trim().toLowerCase();
    return (pendingLinks || []).filter(
        (link) => link?.accountCodeId != null && String(link.accountCodeId).trim().toLowerCase() === wanted);
};

/**
 * Translates pending links into the fields the generate endpoints already accept: every source id,
 * per-source amounts for GeneratedFrom links (the backend ignores them for payments, which it
 * allocates by invoice balance), and the first paid invoice as the legacy RelatedIncomeId.
 */
export const pendingLinksToGeneratePayload = (pendingLinks = []) => {
    const sourceDocumentIds = [];
    const sourceAllocations = [];
    let relatedIncomeId = null;
    (pendingLinks || []).forEach((link) => {
        if (!link || link.documentId == null) return;
        sourceDocumentIds.push(link.documentId);
        if (Number(link.relationType) === RELATION_TYPE.GENERATED_FROM) {
            sourceAllocations.push({ sourceDocumentId: link.documentId, amount: roundAmount(link.appliedAmount) });
        } else if (Number(link.relationType) === RELATION_TYPE.PAYMENT_FOR && relatedIncomeId == null) {
            relatedIncomeId = link.documentId;
        }
    });
    return { sourceDocumentIds, sourceAllocations, relatedIncomeId };
};

/**
 * Root document Description for a create-mode "שיוך מסמכים" generate. Same shape as
 * generateDescription in TransactionDocuments (`הופק מ… מספר …`) but `שויך מ` because the
 * documents were linked, not converted.
 *
 * @param {Array} pendingLinks
 * @param {(type: number) => string} typeName  Label for TransactionDocumentType (e.g. Constants.TransactionDocuments[type]).
 * @param {string} [fallbackName]
 */
export const pendingLinksToLinkedDescription = (pendingLinks = [], typeName, fallbackName = 'מסמך') => {
    const links = (pendingLinks || []).filter((link) => link && (link.reference || link.reference === 0));
    if (links.length === 0) return '';
    const sourceTypeName = typeName?.(Number(links[0].documentType)) || fallbackName;
    const refs = links.map((link) => link.reference).join(', ');
    return `שויך מ${sourceTypeName} מספר ${refs}`;
};

/** Translation key describing what an anchor links to. */
export const anchorRelationLabelKey = (anchor) => {
    if (!anchor) return '';
    if (anchor.relationType === RELATION_TYPE.PAYMENT_FOR) {
        return anchor.direction === LINK_DIRECTION.AS_SOURCE ? 'LinkDocumentsLinkReceipt' : 'LinkDocumentsLinkInvoice';
    }
    return anchor.direction === LINK_DIRECTION.AS_SOURCE ? 'LinkDocumentsLinkInvoiceToSource' : 'LinkDocumentsLinkSource';
};

/** { code, message } from a failed link response body (ApiResponseDTO). */
export const parseLinkError = (body) => {
    let parsed = body;
    if (typeof body === 'string') {
        try {
            parsed = JSON.parse(body);
        } catch {
            return { code: null, message: body };
        }
    }
    const data = pick(parsed, 'data', 'Data');
    return {
        code: pick(data, 'code', 'Code') ?? null,
        message: pick(parsed, 'message', 'Message') ?? '',
    };
};

/**
 * Shared "שיוך מסמכים" state for the document-creation pages (create mode). It owns the pending
 * links chosen before the document is saved, keeps them consistent with the current form
 * (customer / amount), and turns them into the fields the generate endpoints already accept.
 *
 * Nothing is persisted here: the links are only applied when the creation form generates the
 * document and sends `SourceDocumentIds` / `SourceAllocations` / `RelatedIncomeId`.
 *
 * @param {Object} params
 * @param {number} params.entityType           DocumentEntityType of the document being created.
 * @param {number} params.documentType         TransactionDocumentType of the document being created.
 * @param {string} params.customerAccountCodeId Selected customer's account-card id.
 * @param {string} [params.customerName]        Customer display name (shown in the dialog header).
 * @param {number} params.amount               Current document amount (settlement amount for a receipt).
 * @param {number} [params.currency]           ForeignCurrencyCode (default ILS).
 * @param {boolean} [params.enabled]           Whether linking is offered for this type.
 */
export const useDocumentLinking = ({
    entityType,
    documentType,
    customerAccountCodeId,
    customerName,
    amount,
    currency,
    items,
    enabled = true,
}) => {
    const [pendingLinks, setPendingLinks] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);

    // Changing the customer must never keep another customer's links (validation rule 13 / 27).
    useEffect(() => {
        setPendingLinks((prev) => {
            if (prev.length === 0) return prev;
            const kept = pendingLinksForCustomer(prev, customerAccountCodeId);
            return kept.length === prev.length ? prev : kept;
        });
    }, [customerAccountCodeId]);

    const itemsKey = JSON.stringify(Array.isArray(items) ? items : []);

    const draft = useMemo(() => ({
        entityType,
        documentType,
        customerAccountCodeId: customerAccountCodeId ? String(customerAccountCodeId) : '',
        customerName: customerName || '',
        amount: Number(amount) || 0,
        currency: resolveLinkCurrency(currency),
        items: Array.isArray(items) ? items : [],
    }), [entityType, documentType, customerAccountCodeId, customerName, amount, currency, itemsKey]);

    useEffect(() => {
        setPendingLinks((prev) => {
            if (prev.length === 0) return prev;
            const next = revalidatePendingLinkMatches(prev, items || [], amount, documentType);
            return next.some((link, i) => link !== prev[i]) ? next : prev;
        });
    }, [itemsKey, amount]);

    const canOpen = !!enabled && !!customerAccountCodeId && Number(amount) > 0;

    const openModal = useCallback(() => {
        if (canOpen) setModalOpen(true);
    }, [canOpen]);
    const closeModal = useCallback(() => setModalOpen(false), []);

    const confirm = useCallback((links) => {
        setPendingLinks(Array.isArray(links) ? links : []);
    }, []);

    const removeLink = useCallback((documentId) => {
        setPendingLinks((prev) => prev.filter((l) => String(l.documentId) !== String(documentId)));
    }, []);

    const setLinkItems = useCallback((documentId, nextItems) => {
        setPendingLinks((prev) => {
            const updated = prev.map((link) =>
                String(link.documentId) === String(documentId) ? { ...link, items: nextItems } : link);
            return revalidatePendingLinkMatches(updated, items || [], amount, documentType);
        });
    }, [items, amount, documentType]);

    const clear = useCallback(() => setPendingLinks([]), []);

    const validation = useMemo(
        () => validatePendingLinks(pendingLinks, Number(amount) || 0),
        [pendingLinks, amount]);

    const payload = useMemo(() => pendingLinksToGeneratePayload(pendingLinks), [pendingLinks]);

    return {
        pendingLinks,
        setPendingLinks,
        draft,
        modalOpen,
        canOpen,
        openModal,
        closeModal,
        confirm,
        removeLink,
        setLinkItems,
        clear,
        total: pendingLinksTotal(pendingLinks),
        validation,
        /** { sourceDocumentIds, sourceAllocations, relatedIncomeId } for the generate request. */
        payload,
        count: pendingLinks.length,
    };
};

